import { useQueryClient } from '@tanstack/react-query';
import { toastNotifications } from '@uag/react-core';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { getGetProjectsQueryKey, useDeleteProject } from 'api/queries/projects/projects';
import { ProjectsDialog } from 'app/modules/wallforming/components/projects-manager/types/projectDialog';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { ProjectReference } from 'app/modules/wallforming/components/projects-manager/types/projectReference';

interface UseProjectDeleteInput {
    resolvedActiveProject: ProjectReference | null;
}

/**
 * Handles the project-delete flow:
 * - Manages `deleteCandidate` state for the confirm-before-delete dialog.
 * - `handleDeleteProject` — calls the delete API, invalidates the project list
 *   cache, and clears the active-project store reference (including the `projectId`
 *   URL param) when the deleted project was the currently active one.
 */
export const useProjectDelete = ({ resolvedActiveProject }: UseProjectDeleteInput) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    const [deleteCandidate, setDeleteCandidate] = useState<{ id: string; name: string } | null>(null);

    const deleteProjectMutation = useDeleteProject();

    const { setActiveProject, setLastSavedSignature, setActiveDialog } = usePlacementStore(
        useShallow((state) => ({
            setActiveProject: state.setActiveProject,
            setLastSavedSignature: state.setLastSavedSignature,
            setActiveDialog: state.setActiveProjectDialog
        }))
    );

    const handleDeleteProject = useCallback(
        async (projectId: string, projectName: string) => {
            try {
                await deleteProjectMutation.mutateAsync({ projectId });
                await queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey() });

                if (resolvedActiveProject?.id === projectId) {
                    setActiveProject(null);
                    setLastSavedSignature('');
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.delete('projectId');
                    window.history.replaceState(null, '', `${window.location.pathname}?${nextParams.toString()}`);
                }

                toastNotifications.success({
                    title: t('success'),
                    message: t('infoProjectsDeleteSuccess', { projectName })
                });
                setDeleteCandidate(null);
                setActiveDialog(ProjectsDialog.NONE);
            } catch (error) {
                toastNotifications.error({
                    title: t('error'),
                    message: error instanceof Error ? error.message : t('errorDeleteProject')
                });
            }
        },
        [
            deleteProjectMutation,
            queryClient,
            resolvedActiveProject,
            searchParams,
            setActiveDialog,
            setActiveProject,
            setLastSavedSignature,
            t
        ]
    );

    const openDeleteDialog = useCallback(
        (candidate: { id: string; name: string }) => {
            setDeleteCandidate(candidate);
            setActiveDialog(ProjectsDialog.DELETE);
        },
        [setActiveDialog]
    );

    return {
        deleteCandidate,
        setDeleteCandidate,
        isDeletePending: deleteProjectMutation.isPending,
        handleDeleteProject,
        openDeleteDialog
    };
};
